import { PageBackLink } from "@/components/page-back-link"
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { DataGridColumn } from "@/components/data-grid/data-grid"
import { StatusBadge } from '@/components/status-badge'
import { Badge } from '@/components/ui/badge'
import { DataGrid } from '@/components/data-grid'
import { Pagination } from '@/components/pagination'
import { ErrorBanner, ErrorBoundary } from '@/components/error-boundary'
import { PageBreadcrumbs, PageHeader } from '@/components/page-header'
import { OrderListFilters } from '@/features/orders/components/OrderListFilters'
import { getWorkflowTemplateLabel, resolveJourneyTemplates } from '@/features/orders/order-intake-path'
import { formatOrderStatusBadgeLabel, getOrderStatusBadgeVariant } from '@/features/orders/order-status'
import { OrderListSearchSchema } from '@/features/orders/order-list-filters'
import { queryKeys } from '@/lib/query-keys'
import { api } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { getRowIndexProps } from '@/lib/pagination'
import type { components } from '@optimist/api-contracts'
import { OrderSortFieldSchema, type PortalOrderListItem } from '@optimist/schemas'

type PaginationMeta = components['schemas']['PaginationMeta']
type OrderRow = PortalOrderListItem

/** Orders queue page. */
export const Route = createFileRoute('/dashboard/orders/')({
  component: OrdersQueuePage,
  validateSearch: (search) => OrderListSearchSchema.parse(search),
})

function OrdersQueuePage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const filters = Route.useSearch()
  const { page, limit } = filters

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.orders.list(filters),
    queryFn: () =>
      api.get<{ data: OrderRow[]; meta: PaginationMeta }>('/orders', {
        params: filters,
      }),
  })

  const onRowClicked = (row: OrderRow) => {
    navigate({ to: '/dashboard/orders/$orderId', params: { orderId: row.id } })
  }

  const getPatientName = (orderRow: OrderRow | undefined | null): string => {
    const firstName = orderRow?.patientFirstName?.trim()
    const lastName = orderRow?.patientLastName?.trim()

    if (firstName && lastName) return `${firstName} ${lastName}`
    if (firstName) return firstName
    if (lastName) return lastName
    return 'Restricted'
  }

  const columnDefs: DataGridColumn<OrderRow>[] = [
    {
      field: 'id',
      colId: 'id',
      headerName: 'Order ID',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-foreground-secondary">{row.id}</span>
      ),
    },
    {
      colId: 'patient',
      headerName: 'Patient',
      sortable: true,
      render: (row) => getPatientName(row),
    },
    {
      headerName: 'Treatment',
      field: 'treatment',
      colId: 'treatment',
      sortable: true,
      render: (row) => (
        <Badge variant="secondary">
          {typeof row.treatment === "string" ? row.treatment : "Not Selected"}
        </Badge>
      ),
    },
    {
      colId: 'workflow',
      headerName: 'Workflow',
      sortable: true,
      render: (row) => {
        const order = row
        if (order === undefined) return <span>—</span>
        const templates = resolveJourneyTemplates({
          journeyDefinitionId: order.journeyDefinitionId,
          journeyDefinitionVersion: order.journeyDefinitionVersion,
        })
        const workflowLabel = getWorkflowTemplateLabel(templates.workflowTemplate)
        return <StatusBadge variant="info">{workflowLabel}</StatusBadge>
      },
    },
    {
      field: 'displayStatus',
      colId: 'status',
      headerName: 'Status',
      sortable: true,
      render: (row) => {
        const rawValue = row.displayStatus
        return (
          <StatusBadge variant={getOrderStatusBadgeVariant(rawValue)}>
            {formatOrderStatusBadgeLabel(rawValue)}
          </StatusBadge>
        )
      },
    },
    {
      field: 'createdAt',
      colId: 'createdAt',
      headerName: 'Created',
      sortable: true,
      render: (row) => typeof row.createdAt === 'string' ? formatDateTime(row.createdAt) : '-',
    },
    {
      field: 'updatedAt',
      colId: 'updatedAt',
      headerName: 'Updated',
      sortable: true,
      render: (row) => typeof row.updatedAt === 'string' ? formatDateTime(row.updatedAt) : '-',
    },
  ]

  const response = data
  const orders = response ? response.data : []
  const meta = response ? response.meta : null
  const rowIndexProps = getRowIndexProps({ meta, page, limit, mode: 'descending' })
  const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
  const handleRetry = () => {
    void refetch()
  }

  return (
    <ErrorBoundary
      title="Orders page error"
      fallback={<ErrorBanner title="Orders unavailable" message="Something went wrong loading this page." />}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <PageHeader back={<PageBackLink to="/dashboard" label="Back to dashboard" />}
            breadcrumbs={
              <PageBreadcrumbs
                items={[
                  <Link key="dashboard" to="/dashboard">Dashboard</Link>,
                  <span key="orders">Orders</span>,
                ]}
              />
            }
            title="Orders"
          />

        </div>

        <OrderListFilters
          key={JSON.stringify([
            filters.search,
            filters.status,
            filters.journeyDefinitionId,
          ])}
          filters={filters}
          onApply={(nextFilters) => {
            navigate({
              search: {
                ...nextFilters,
                sortBy: filters.sortBy,
                sortDirection: filters.sortDirection,
                page: 1,
                limit,
              },
            })
          }}
          onClear={() => {
            navigate({
              search: {
                sortBy: filters.sortBy,
                sortDirection: filters.sortDirection,
                page: 1,
                limit,
              },
            })
          }}
        />

        {error ? (
          <ErrorBanner title="Error loading orders" message={errorMessage} onRetry={handleRetry} />
        ) : null}

        <ErrorBoundary
          resetKey={orders.length}
          fallback={<ErrorBanner title="Orders table failed" message="Unable to render order data." />}
        >
          <DataGrid<OrderRow>
            rowData={orders}
            columnDefs={columnDefs}
            loading={isLoading}
            {...rowIndexProps}
            onRowClicked={onRowClicked}
            sort={{ column: filters.sortBy, direction: filters.sortDirection }}
            onSortChange={(nextSort) => {
              const sortBy = OrderSortFieldSchema.parse(nextSort.column)
              navigate({
                search: (current) => ({
                  ...current,
                  sortBy,
                  sortDirection: nextSort.direction,
                  page: 1,
                }),
              })
            }}
          />
        </ErrorBoundary>

        {meta ? (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.totalPages}
            onPageChange={(nextPage) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: nextPage,
                  limit,
                }),
              })
            }}
            onLimitChange={(nextLimit) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: 1,
                  limit: nextLimit,
                }),
              })
            }}
          />
        ) : null}

      </div>
    </ErrorBoundary>
  )
}
